import {
    Component,
    DestroyRef,
    forwardRef,
    inject,
    input,
    OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, tap } from 'rxjs';

/**
 * Caixa de busca com debounce, integrável a formulários (`ControlValueAccessor`).
 *
 * Notifica a mudança de valor somente após {@link valueChangeDelay}
 * milissegundos sem digitação, evitando disparos excessivos.
 */
@Component({
    selector: 'conversai-search-box',
    templateUrl: './search-box.component.html',
    imports: [FormsModule, ReactiveFormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchBoxComponent),
            multi: true,
        },
    ],
})
export class SearchBoxComponent implements OnInit, ControlValueAccessor {
    destroyRef: DestroyRef = inject(DestroyRef);

    /** Texto de placeholder do campo. */
    placeholder = input<string>();

    /** Ícone exibido na caixa de busca. */
    icon = input<string>();

    /** Atraso (ms) do debounce antes de notificar a mudança de valor. */
    valueChangeDelay = input<number>(1000);

    /** Controle interno do campo de busca. */
    formControl = new FormControl<string>('');

    // Funções de callback de ControlValueAccessor
    onChanged: (value: string) => void = () => {};
    onTouched: () => void = () => {};

    ngOnInit(): void {
        this.formControl.valueChanges
            .pipe(
                debounceTime(this.valueChangeDelay()),
                tap((value) => this.onChanged(value ? value : '')),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    writeValue(value: string): void {
        this.formControl.setValue(value, { emitEvent: false });
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.formControl.disable() : this.formControl.enable();
    }
}
